/**
 * Gateway Service
 *
 * Runs the CLI as a gateway node that polls for remote messages
 * from web/mobile clients, processes them locally with full MCP
 * tool access, and relays streaming events back through the server.
 */

import { EventEmitter } from 'events';
import os from 'os';
import { agentService, extractErrorMessage } from '../agent/service.js';
import { initializeAgent } from '../agent/utils.js';
import type { McpToolResult, AgentStreamCallbacks, McpState } from '../agent/types.js';
import { executeSystemTool } from '../mcp/system.js';
import { sendHeartbeat, type WorkerStatus as ApiWorkerStatus } from '../api/worker-api.js';
import {
    pollGatewayRequests,
    claimGatewayRequest,
    pushGatewayEvents,
    completeGatewayRequest,
    registerGatewayInstance,
    unregisterGatewayInstance,
    type GatewayRequest,
    type GatewayStreamEvent,
    type GatewayCommandFromServer,
} from '../api/gateway-api.js';
import { CLI_VERSION } from '../version.js';
import * as fs from 'fs/promises';
import * as path from 'path';

export interface GatewayConfig {
    pollInterval: number;
    eventBatchInterval: number;
    requestTimeout: number;
    debug?: boolean;
    sessionName?: string;
}

const DEFAULT_CONFIG: GatewayConfig = {
    pollInterval: 3000,         // 3 seconds — fast for interactive feel
    eventBatchInterval: 500,    // 500ms event batch push
    requestTimeout: 600000,     // 10 minutes
    debug: false,
};

export type GatewayStatus = 'idle' | 'running' | 'stopping';

export interface GatewayState {
    status: GatewayStatus;
    currentRequest?: GatewayRequest;
    completedCount: number;
    failedCount: number;
    startedAt?: Date;
    lastPollAt?: Date;
}

export type GatewayEvent =
    | { type: 'started' }
    | { type: 'stopped' }
    | { type: 'request_claimed'; request: GatewayRequest }
    | { type: 'request_completed'; request: GatewayRequest; durationMs: number }
    | { type: 'request_failed'; request: GatewayRequest; error: string }
    | { type: 'poll_empty' }
    | { type: 'error'; error: string }
    | { type: 'remote_content'; text: string; partial: boolean }
    | { type: 'remote_tool_call'; id: string; name: string; serverName?: string; isMcp: boolean }
    | { type: 'remote_tool_result'; id: string; name: string; status: 'completed' | 'error' }
    | { type: 'remote_done'; conversationId?: string }
    | { type: 'remote_error'; error: string };

export class GatewayService extends EventEmitter {
    private config: GatewayConfig;
    private state: GatewayState;
    private pollTimer: ReturnType<typeof setInterval> | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private abortController: AbortController | null = null;

    private mcpState: unknown = null;
    private onMcpToolCall: unknown = null;
    private mcpClient: unknown = null;

    private sessionId?: string;
    private deviceName: string;
    private sessionName?: string;

    constructor(config: Partial<GatewayConfig> = {}) {
        super();
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.state = {
            status: 'idle',
            completedCount: 0,
            failedCount: 0,
        };
        this.sessionName = config.sessionName;
        this.deviceName = this.sessionName
            ? `${this.sessionName} @ ${os.hostname()}`
            : `CLI Gateway @ ${os.hostname()}`;
    }

    getState(): GatewayState {
        return { ...this.state };
    }

    isProcessingRemote(): boolean {
        return !!this.state.currentRequest;
    }

    /**
     * Supply externally-initialized MCP state.
     * When running in unified mode, the caller initializes MCP once
     * and passes it in before calling start().
     */
    setMcpState(mcpState: unknown, onMcpToolCall: unknown, mcpClient: unknown): void {
        this.mcpState = mcpState;
        this.onMcpToolCall = onMcpToolCall;
        this.mcpClient = mcpClient;
    }

    async start(): Promise<void> {
        if (this.state.status === 'running') {
            throw new Error('Gateway is already running');
        }

        this.log('Starting gateway...');

        // Generate a unique per-launch session ID so multiple CLIs are distinguishable
        if (!this.sessionId) {
            const crypto = await import('crypto');
            const launchNonce = crypto.randomBytes(8).toString('hex');
            this.sessionId = `cli-gw-${os.hostname().slice(0, 12)}-${launchNonce}`;
        }

        // Initialize MCP for local tool execution (skip if already supplied)
        if (!this.mcpState) {
            try {
                const result = await initializeAgent({
                    mcp: true,
                    debug: this.config.debug,
                });
                this.mcpState = result.mcpState;
                this.onMcpToolCall = result.onMcpToolCall;
                this.mcpClient = result.mcpClient;
            } catch (error) {
                this.log(`Failed to initialize MCP: ${extractErrorMessage(error)}`);
            }
        }

        this.state = {
            ...this.state,
            status: 'running',
            startedAt: new Date(),
        };

        this.abortController = new AbortController();
        this.emit('event', { type: 'started' } as GatewayEvent);

        // Register with cloud backend so frontends and the cloud agent see us
        await this.registerWithCloud();

        this.startHeartbeat();
        this.startPolling();

        this.log('Gateway started');
    }

    private async registerWithCloud(): Promise<void> {
        if (!this.sessionId) return;

        const cliVersion = CLI_VERSION;

        const result = await registerGatewayInstance(this.sessionId, this.deviceName, {
            capabilities: ['gateway', 'mcp', 'file-ops', 'reverse-commands'],
            workingDirectory: process.cwd(),
            platform: `${os.platform()} ${os.arch()}`,
            cliVersion,
            sessionName: this.sessionName,
            protocolVersion: 2,
        });

        if (!result.success) {
            this.log(`Registration failed: ${result.error}`);
        } else {
            this.log('Registered with cloud backend');
        }
    }

    async stop(): Promise<void> {
        if (this.state.status === 'idle') return;

        this.log('Stopping gateway...');
        this.state.status = 'stopping';

        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }

        // Disconnect MCP
        if (this.mcpClient && typeof (this.mcpClient as any).disconnectAll === 'function') {
            try {
                await (this.mcpClient as any).disconnectAll();
            } catch { /* ignore */ }
        }

        // Unregister from cloud backend and send final heartbeat as idle
        if (this.sessionId) {
            await unregisterGatewayInstance(this.sessionId).catch(() => { /* ignore */ });
            await sendHeartbeat(this.sessionId, {
                status: 'idle',
                completedCount: this.state.completedCount,
                failedCount: this.state.failedCount,
            }).catch(() => { /* ignore */ });
        }

        this.state = { ...this.state, status: 'idle', currentRequest: undefined };
        this.emit('event', { type: 'stopped' } as GatewayEvent);
        this.removeAllListeners();
        this.log('Gateway stopped');
    }

    private startHeartbeat(): void {
        this.sendHeartbeatUpdate();
        this.heartbeatTimer = setInterval(() => {
            this.sendHeartbeatUpdate();
        }, 30000);
    }

    private async sendHeartbeatUpdate(): Promise<void> {
        if (!this.sessionId) return;
        try {
            const status: ApiWorkerStatus = this.state.currentRequest ? 'running' : 'idle';

            // Re-register on each heartbeat to keep the in-memory registry alive
            await registerGatewayInstance(this.sessionId, this.deviceName, {
                capabilities: ['gateway', 'mcp'],
                workingDirectory: process.cwd(),
                platform: `${os.platform()} ${os.arch()}`,
                sessionName: this.sessionName,
            }).catch(() => { /* non-critical */ });

            const result = await sendHeartbeat(this.sessionId, {
                status,
                completedCount: this.state.completedCount,
                failedCount: this.state.failedCount,
                workingDirectory: process.cwd(),
                capabilities: ['gateway', 'mcp'],
            });
            if (!result.success) {
                const msg = result.error || 'Unknown error';
                this.emit('event', { type: 'error', error: `Heartbeat failed: ${msg}` } as GatewayEvent);
            }
        } catch (error) {
            const msg = extractErrorMessage(error);
            this.emit('event', { type: 'error', error: `Heartbeat failed: ${msg}` } as GatewayEvent);
        }
    }

    private startPolling(): void {
        this.pollAndProcess();
        this.pollTimer = setInterval(() => {
            if (this.state.status === 'running' && !this.state.currentRequest) {
                this.pollAndProcess();
            }
        }, this.config.pollInterval);
    }

    private async pollAndProcess(): Promise<void> {
        if (this.state.status !== 'running' || !this.sessionId) return;

        this.state.lastPollAt = new Date();

        try {
            const { requests } = await pollGatewayRequests(this.sessionId);

            if (requests.length === 0) {
                this.emit('event', { type: 'poll_empty' } as GatewayEvent);
                return;
            }

            // Try to claim the first request
            for (const req of requests) {
                const claimResult = await claimGatewayRequest(
                    req.requestId, this.sessionId, this.deviceName
                );

                if (claimResult.success && claimResult.request) {
                    this.log(`Claimed request: ${claimResult.request.requestId}`);
                    this.emit('event', { type: 'request_claimed', request: claimResult.request } as GatewayEvent);
                    await this.processRequest(claimResult.request);
                    return;
                }
                this.log(`Failed to claim ${req.requestId}: ${claimResult.error}`);
            }
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            this.emit('event', { type: 'error', error: msg } as GatewayEvent);
            this.log(`Poll error: ${msg}`);
        }
    }

    /**
     * Process a claimed gateway request by running it through the agent
     * with local MCP tools, and relay events back to the server.
     */
    private async processRequest(request: GatewayRequest): Promise<void> {
        this.state.currentRequest = request;
        const startTime = Date.now();

        // Event buffer for batched pushing
        let eventBuffer: GatewayStreamEvent[] = [];
        let batchTimer: ReturnType<typeof setInterval> | null = null;

        // Async command queue — commands are processed without blocking event flush
        const commandQueue: GatewayCommandFromServer[] = [];
        let processingCommands = false;

        const processCommandQueue = async () => {
            if (processingCommands) return;
            processingCommands = true;
            while (commandQueue.length > 0) {
                const cmd = commandQueue.shift()!;
                try {
                    await this.handleIncomingCommand(cmd, eventBuffer);
                } catch (err) {
                    this.log(`Command handling failed: ${extractErrorMessage(err)}`);
                }
            }
            processingCommands = false;
        };

        const flushEvents = async () => {
            if (!this.sessionId) return;
            const batch = [...eventBuffer];
            eventBuffer = [];
            try {
                const result = await pushGatewayEvents(request.requestId, this.sessionId, batch);
                if (result.commands?.length) {
                    commandQueue.push(...result.commands);
                    processCommandQueue();
                }
            } catch (err) {
                this.log(`Event push failed: ${extractErrorMessage(err)}`);
            }
        };

        // Start batch flush timer
        batchTimer = setInterval(() => { flushEvents(); }, this.config.eventBatchInterval);

        // Periodic state_update push (model info, cost)
        const stateTimer = setInterval(() => {
            eventBuffer.push({
                type: 'state_update',
                data: {
                    model: 'gateway',
                    permissionMode: 'autonomous',
                    autoEdit: true,
                },
            });
        }, 10000);

        return new Promise<void>((resolve) => {
            let responseText = '';
            let hasError = false;
            let errorMessage = '';
            let settled = false;
            const toolCalls: unknown[] = [];

            const timeout = setTimeout(async () => {
                if (settled) return;
                settled = true;
                clearInterval(stateTimer);
                agentService.abort();
                await this.finishRequest(request, false, '', 'Request timed out', startTime, batchTimer, flushEvents);
                resolve();
            }, this.config.requestTimeout);

            const callbacks: AgentStreamCallbacks = {
                onContent: (text: string, partial: boolean) => {
                    if (partial) {
                        responseText += text;
                    } else {
                        responseText = text;
                    }
                    eventBuffer.push({
                        type: 'content',
                        data: { text, partial },
                    });
                    this.emit('event', { type: 'remote_content', text, partial } as GatewayEvent);
                },

                onThinking: (isThinking: boolean) => {
                    eventBuffer.push({
                        type: 'progress',
                        data: { stage: isThinking ? 'thinking' : 'ready', detail: 'Model is reasoning...' },
                    });
                },

                onToolCall: (id: string, name: string) => {
                    toolCalls.push({ id, name, status: 'pending' });
                    eventBuffer.push({
                        type: 'tool_call',
                        data: { id, name, status: 'pending' },
                    });
                    this.emit('event', { type: 'remote_tool_call', id, name, isMcp: false } as GatewayEvent);
                },

                onToolResult: (id: string, name: string, result?: unknown) => {
                    const tc = toolCalls.find((t: any) => t.id === id) as any;
                    if (tc) tc.status = 'completed';
                    const isError = result && typeof result === 'object' &&
                        (result as Record<string, unknown>).isError === true;
                    eventBuffer.push({
                        type: 'tool_result',
                        data: { id, name, status: 'completed', result },
                    });
                    this.emit('event', { type: 'remote_tool_result', id, name, status: isError ? 'error' : 'completed' } as GatewayEvent);
                },

                onMcpToolCall: async (
                    callId: string,
                    serverName: string,
                    toolName: string,
                    args: Record<string, unknown>
                ): Promise<McpToolResult> => {
                    eventBuffer.push({
                        type: 'mcp_tool_call',
                        data: { callId, serverName, toolName },
                    });
                    this.emit('event', { type: 'remote_tool_call', id: callId, name: toolName, serverName, isMcp: true } as GatewayEvent);

                    let mcpResult: McpToolResult;

                    // Execute locally via MCP client
                    if (typeof this.onMcpToolCall === 'function') {
                        try {
                            mcpResult = await (this.onMcpToolCall as any)(callId, serverName, toolName, args);
                        } catch (error) {
                            mcpResult = {
                                content: [{ type: 'text' as const, text: `Error: ${extractErrorMessage(error)}` }],
                                isError: true,
                            };
                        }
                    } else if (toolName.startsWith('paean_execute') || toolName.startsWith('paean_check') || toolName.startsWith('paean_kill')) {
                        // Fallback to system tools
                        const result = await executeSystemTool(toolName, args, {
                            autonomousMode: true,
                            debug: this.config.debug,
                        }) as { success: boolean;[key: string]: unknown };
                        mcpResult = {
                            content: [{ type: 'text' as const, text: JSON.stringify(result) }],
                            isError: !result.success,
                        };
                    } else {
                        mcpResult = {
                            content: [{ type: 'text' as const, text: 'MCP not available' }],
                            isError: true,
                        };
                    }

                    this.emit('event', { type: 'remote_tool_result', id: callId, name: toolName, status: mcpResult.isError ? 'error' : 'completed' } as GatewayEvent);
                    return mcpResult;
                },

                onError: (error: string) => {
                    hasError = true;
                    errorMessage = error;
                    clearTimeout(timeout);
                    clearInterval(stateTimer);
                    eventBuffer.push({
                        type: 'error',
                        data: { error },
                    });
                    this.emit('event', { type: 'remote_error', error } as GatewayEvent);
                },

                onDone: async (convId?: string) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timeout);
                    clearInterval(stateTimer);

                    eventBuffer.push({
                        type: 'done',
                        data: { conversationId: convId },
                    });

                    this.emit('event', { type: 'remote_done', conversationId: convId } as GatewayEvent);

                    if (hasError) {
                        await this.finishRequest(request, false, responseText, errorMessage, startTime, batchTimer, flushEvents);
                    } else {
                        await this.finishRequest(request, true, responseText, undefined, startTime, batchTimer, flushEvents, toolCalls);
                    }
                    resolve();
                },
            };

            agentService.streamMessage(request.message, callbacks, {
                conversationId: request.conversationHashKey,
                mcpState: this.mcpState as McpState | undefined,
                cliMode: {
                    enabled: true,
                    cwd: process.cwd(),
                    platform: process.platform,
                    hostname: os.hostname(),
                    channel: 'gateway',
                },
            });
        });
    }

    private async handleIncomingCommand(
        cmd: GatewayCommandFromServer,
        eventBuffer: GatewayStreamEvent[],
    ): Promise<void> {
        const cwd = process.cwd();
        switch (cmd.type) {
            case 'file_tree_request': {
                const data = cmd.data as { path: string; requestId: string };
                const result = await this.getFileTree(cwd, data.path || '.', data.requestId);
                eventBuffer.push({ type: 'file_tree_response', data: result as unknown as Record<string, unknown> });
                break;
            }
            case 'file_read_request': {
                const data = cmd.data as { path: string; requestId: string };
                const result = await this.getFileContent(cwd, data.path, data.requestId);
                eventBuffer.push({ type: 'file_read_response', data: result as unknown as Record<string, unknown> });
                break;
            }
            case 'slash_command': {
                const data = cmd.data as { command: string };
                this.log(`Received slash command: ${data.command}`);
                break;
            }
            case 'cancel': {
                this.log('Received cancel command');
                if (this.abortController) {
                    this.abortController.abort();
                }
                break;
            }
        }
    }

    private isPathSafe(baseCwd: string, requestedPath: string): string | null {
        const normalizedBase = baseCwd.endsWith(path.sep) ? baseCwd : baseCwd + path.sep;
        const resolved = path.resolve(baseCwd, requestedPath);
        if (resolved !== baseCwd && !resolved.startsWith(normalizedBase)) return null;
        return resolved;
    }

    private static readonly HIDDEN_DIRS = new Set([
        '.git', 'node_modules', '.next', '__pycache__', '.cache', 'dist', '.DS_Store',
    ]);

    private static readonly BINARY_EXTENSIONS = new Set([
        '.png', '.jpg', '.jpeg', '.gif', '.webp', '.ico', '.bmp', '.svg',
        '.mp3', '.mp4', '.avi', '.mov', '.wav', '.ogg',
        '.zip', '.tar', '.gz', '.bz2', '.7z', '.rar',
        '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
        '.exe', '.dll', '.so', '.dylib', '.bin', '.dat',
        '.woff', '.woff2', '.ttf', '.eot', '.otf',
        '.sqlite', '.db',
    ]);

    private static readonly MAX_FILE_READ_SIZE = 100 * 1024;

    private async getFileTree(cwd: string, requestedPath: string, requestId: string) {
        const fullPath = this.isPathSafe(cwd, requestedPath);
        if (!fullPath) {
            return { path: requestedPath, requestId, entries: [], error: 'Access denied: path outside workspace' };
        }
        try {
            const dirents = await fs.readdir(fullPath, { withFileTypes: true });
            const entries: Array<{ name: string; type: 'file' | 'directory'; size: number }> = [];
            for (const dirent of dirents) {
                if (GatewayService.HIDDEN_DIRS.has(dirent.name) || dirent.name.startsWith('.')) continue;
                try {
                    const entryPath = path.join(fullPath, dirent.name);
                    const stat = await fs.stat(entryPath);
                    entries.push({ name: dirent.name, type: dirent.isDirectory() ? 'directory' : 'file', size: stat.size });
                } catch { /* skip */ }
            }
            entries.sort((a, b) => {
                if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
                return a.name.localeCompare(b.name);
            });
            return { path: requestedPath, requestId, entries };
        } catch (err: any) {
            return { path: requestedPath, requestId, entries: [], error: err.message };
        }
    }

    private async getFileContent(cwd: string, requestedPath: string, requestId: string) {
        const fullPath = this.isPathSafe(cwd, requestedPath);
        if (!fullPath) {
            return { path: requestedPath, requestId, error: 'Access denied: path outside workspace' };
        }
        const ext = path.extname(fullPath).toLowerCase();
        if (GatewayService.BINARY_EXTENSIONS.has(ext)) {
            return { path: requestedPath, requestId, error: 'Binary file cannot be displayed' };
        }
        try {
            const stat = await fs.stat(fullPath);
            const truncated = stat.size > GatewayService.MAX_FILE_READ_SIZE;
            const content = await fs.readFile(fullPath, 'utf-8');
            const finalContent = truncated ? content.slice(0, GatewayService.MAX_FILE_READ_SIZE) : content;
            return { path: requestedPath, requestId, content: finalContent, truncated };
        } catch (err: any) {
            return { path: requestedPath, requestId, error: err.message };
        }
    }

    private async finishRequest(
        request: GatewayRequest,
        success: boolean,
        content: string,
        error: string | undefined,
        startTime: number,
        batchTimer: ReturnType<typeof setInterval> | null,
        flushEvents: () => Promise<void>,
        toolCalls?: unknown[]
    ): Promise<void> {
        // Flush remaining events
        await flushEvents();
        if (batchTimer) clearInterval(batchTimer);

        const durationMs = Date.now() - startTime;

        // Report completion to server
        if (this.sessionId) {
            await completeGatewayRequest(request.requestId, this.sessionId, {
                content: content || '',
                toolCalls,
                error,
            }).catch((err) => {
                this.log(`Complete report failed: ${extractErrorMessage(err)}`);
            });
        }

        if (success) {
            this.state.completedCount++;
            this.emit('event', { type: 'request_completed', request, durationMs } as GatewayEvent);
        } else {
            this.state.failedCount++;
            this.emit('event', { type: 'request_failed', request, error: error || 'Unknown' } as GatewayEvent);
        }

        this.state.currentRequest = undefined;
    }

    private log(message: string): void {
        if (this.config.debug) {
            console.error(`[Gateway] ${message}`);
        }
    }
}

// Singleton
let gatewayInstance: GatewayService | null = null;

export function getGateway(config?: Partial<GatewayConfig>): GatewayService {
    if (!gatewayInstance) {
        gatewayInstance = new GatewayService(config);
    }
    return gatewayInstance;
}

export function resetGateway(): void {
    if (gatewayInstance) {
        gatewayInstance.stop().catch(() => { });
        gatewayInstance = null;
    }
}
